import { Goal, Timestamp } from '@griply/types';
import { computeGoalTaskProgress } from '@griply/utils';
import { useSubGoals } from 'features/goals';
import { useTasks } from 'features/tasks';
import { useMemo } from 'react';

import { useTimeframeDifference } from './use-timeframe-difference';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export const useGoalTaskProgress = (
  goal: Goal,
  { startDate, endDate }: Options,
) => {
  const subGoals = useSubGoals(goal.id);
  const allTasks = useTasks(
    { goals: [goal.id, ...subGoals.map(({ id }) => id)] },
    !!goal.id,
  );

  const completedTasks = useMemo(
    () =>
      allTasks
        .filter(({ completedAt }) => !!completedAt)
        .sort(
          (taskA, taskB) =>
            taskA.completedAt!.getTime() - taskB.completedAt!.getTime(),
        ),
    [allTasks],
  );

  const nonHabitTaskCount = useMemo(
    () => allTasks.filter(({ schedules }) => !schedules?.length).length,
    [allTasks],
  );

  const { entries, target } = useMemo(
    () =>
      computeGoalTaskProgress({
        goal,
        completedTasks,
        allTaskCount: nonHabitTaskCount,
      }),
    [goal, completedTasks, nonHabitTaskCount],
  );

  const timeframeDifference = useTimeframeDifference({
    entries,
    startDate,
    endDate,
    target,
  });

  const completionPercentage =
    Math.floor((completedTasks.length / allTasks.length) * 100) || 0;

  return {
    allTasks,
    entries,
    target,
    completionPercentage,
    timeframeDifference,
  };
};
